import React from "react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  contentfulImage,
  contentfulPictureSources,
  contentfulSrcSet,
} from "../../Utils";

dayjs.extend(relativeTime);

// card renders at 320px wide on md+, full viewport width on mobile
const THUMBNAIL_WIDTHS = [320, 480, 640, 960];
const THUMBNAIL_ASPECT_RATIO = 16 / 9;
// mobile width is the viewport minus the page padding (px-4) and the news
// panel padding (p-6) on both sides
const THUMBNAIL_SIZES = "(min-width: 768px) 320px, calc(100vw - 80px)";

const NewsCard = ({ news }) => {
  const { t } = useTranslation();
  const sources = contentfulPictureSources(news.imageUrl, THUMBNAIL_WIDTHS, {
    aspectRatio: THUMBNAIL_ASPECT_RATIO,
  });

  const formatDate = (dateString) => {
    return dayjs(dateString).fromNow();
  };

  const truncateDescription = (description, maxLength = 120) => {
    if (description.length <= maxLength) return description;
    return description.substring(0, maxLength) + "...";
  };

  return (
    <div className="bg-white border border-slate-200/60 rounded-lg overflow-hidden hover:shadow-md transition-shadow duration-200">
      <div className="aspect-[16/9] overflow-hidden">
        <picture className="block w-full h-full">
          {sources.map(({ type, srcSet }) => (
            <source
              key={type}
              type={type}
              srcSet={srcSet}
              sizes={THUMBNAIL_SIZES}
            />
          ))}
          <img
            src={contentfulImage(news.imageUrl, {
              width: 640,
              height: 360,
              format: null,
            })}
            srcSet={contentfulSrcSet(news.imageUrl, THUMBNAIL_WIDTHS, {
              aspectRatio: THUMBNAIL_ASPECT_RATIO,
              format: null,
            })}
            sizes={THUMBNAIL_SIZES}
            width={640}
            height={360}
            loading="lazy"
            decoding="async"
            alt={news.title}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          />
        </picture>
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
            {news.source}
          </span>
          <span className="text-xs text-slate-500">
            {formatDate(news.publishedAt)}
          </span>
        </div>
        <h3 className="font-semibold text-sm text-slate-900 mb-2 line-clamp-2 leading-tight">
          {news.title}
        </h3>
        <div className="text-xs text-slate-600 mb-3 line-clamp-3 prose prose-xs max-w-none">
          <Markdown
            components={{
              p: ({ children }) => <span>{children}</span>,
              strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
              em: ({ children }) => <em className="italic">{children}</em>,
              a: ({ href, children }) => (
                <a href={href} className="text-blue-600 hover:text-blue-700" target="_blank" rel="noopener noreferrer">
                  {children}
                </a>
              ),
            }}
          >
            {truncateDescription(news.description)}
          </Markdown>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">
            {t("By")} {news.author}
          </span>
          <a
            href={news.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors duration-200"
          >
            {t("Read More")} →
          </a>
        </div>
      </div>
    </div>
  );
};

export default NewsCard;